// ════════════════════════════════════════════════════════════════════════════
// 🔢 Numpad — Phase 2.3 Touch-friendly numeric keypad
// ════════════════════════════════════════════════════════════════════════════
// Imperative API:
//   const val = await openNumpad({ title: 'Cash received', initial: '0', suffix: 'Rs.' });
//   if (val !== null) setCashAmount(val);
// 60px touch targets, haptic feedback per keystroke.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Delete, Check } from 'lucide-react';
import { Haptics } from '../../utils/haptics';
import { SoundManager } from '../../utils/SoundManager';

interface NumpadOptions {
  title: string;
  initial?: string;
  suffix?: string;
  allowDecimal?: boolean;
  max?: number;
}

type Resolver = (value: string | null) => void;

let resolverRef: Resolver | null = null;
let setStateRef: ((next: { open: boolean; opts: NumpadOptions | null }) => void) | null = null;

export function openNumpad(opts: NumpadOptions): Promise<string | null> {
  return new Promise((resolve) => {
    resolverRef = resolve;
    if (setStateRef) setStateRef({ open: true, opts });
    else resolve(null);
  });
}

export const NumpadHost: React.FC = () => {
  const [state, setState] = useState<{ open: boolean; opts: NumpadOptions | null }>({ open: false, opts: null });
  const [val, setVal] = useState('');

  useEffect(() => {
    setStateRef = setState;
    return () => { if (setStateRef === setState) setStateRef = null; };
  }, []);

  useEffect(() => {
    if (state.open) setVal(state.opts?.initial || '');
  }, [state.open, state.opts]);

  const close = (result: string | null) => {
    if (resolverRef) { resolverRef(result); resolverRef = null; }
    setState({ open: false, opts: null });
  };

  // Keyboard support
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { close(null); return; }
      if (e.key === 'Enter') { close(val); return; }
      if (e.key === 'Backspace') { push('back'); return; }
      if (/^[0-9]$/.test(e.key)) push(e.key);
      if (e.key === '.' && state.opts?.allowDecimal !== false) push('.');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.open, val]);

  if (!state.open || !state.opts) return null;

  const opts = state.opts;
  const allowDecimal = opts.allowDecimal !== false;

  const push = (k: string) => {
    Haptics.fire('tap');
    SoundManager.play('ui_click');
    setVal((prev) => {
      if (k === 'back') return prev.slice(0, -1);
      if (k === 'clear') return '';
      if (k === '.') {
        if (!allowDecimal) return prev;
        if (prev.includes('.')) return prev;
        return prev === '' ? '0.' : prev + '.';
      }
      const next = prev + k;
      if (opts.max != null && parseFloat(next) > opts.max) return prev;
      // Cap decimals to 2
      if (next.includes('.') && next.split('.')[1].length > 2) return prev;
      return next;
    });
  };

  const keys: Array<{ label: string; key: string; span?: number; tone?: 'primary' | 'danger' | 'default' }> = [
    { label: '1', key: '1' }, { label: '2', key: '2' }, { label: '3', key: '3' },
    { label: '4', key: '4' }, { label: '5', key: '5' }, { label: '6', key: '6' },
    { label: '7', key: '7' }, { label: '8', key: '8' }, { label: '9', key: '9' },
    { label: allowDecimal ? '.' : '00', key: allowDecimal ? '.' : '00' },
    { label: '0', key: '0' },
    { label: '⌫', key: 'back', tone: 'danger' },
  ];

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="np-bg"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10002] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={(e) => { if (e.target === e.currentTarget) close(null); }}
      >
        <motion.div
          key="np-card"
          initial={{ y: 400, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 400, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <h3 className="text-[12px] font-black text-slate-800 uppercase tracking-wider">{opts.title}</h3>
            <button
              onClick={() => close(null)}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          {/* Display */}
          <div className="px-5 pb-3">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-4 flex items-baseline justify-end gap-2 min-h-[64px]">
              {opts.suffix && <span className="text-[12px] font-mono text-slate-400">{opts.suffix}</span>}
              <span className="text-[28px] sm:text-[32px] font-black font-mono text-slate-800 tabular-nums">
                {val || '0'}
              </span>
            </div>
            <button
              onClick={() => { Haptics.fire('warn'); setVal(''); }}
              className="mt-2 text-[10px] font-bold text-slate-400 hover:text-rose-500 uppercase tracking-wider transition-colors"
            >
              Clear
            </button>
          </div>

          {/* Keys */}
          <div className="px-3 pb-3 grid grid-cols-3 gap-2">
            {keys.map((k) => (
              <motion.button
                key={k.key}
                whileTap={{ scale: 0.92 }}
                onClick={() => push(k.key)}
                className={`h-[60px] rounded-2xl text-[22px] font-black transition-all ${
                  k.tone === 'danger'
                    ? 'bg-rose-50 hover:bg-rose-100 text-rose-500 border border-rose-100'
                    : 'bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-100'
                }`}
                aria-label={k.key === 'back' ? 'Backspace' : k.label}
              >
                {k.key === 'back' ? <Delete size={20} className="mx-auto" /> : k.label}
              </motion.button>
            ))}
          </div>

          {/* Confirm */}
          <div className="px-3 pb-4">
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => { Haptics.fire('success'); close(val || '0'); }}
              className="w-full h-[56px] rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-black text-[14px] uppercase tracking-widest shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2 active:scale-[0.98]"
            >
              <Check size={16} /> Confirm
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
